/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../../../components/v2';
import { renderStatus } from '../../../../../components/v2/cell-renderers/status';
import { useDigitacaoEventos } from '../hooks/useDigitacaoEventos';
import { forcarEnvioIntegracaoNsmp } from '../utils/forcarEnvioIntegracao';
import {
  formatDigitadoEm,
  getEventosResumoFromRow,
  getIntegracaoDetail,
  getIntegracaoStatus,
  getStatusAprovacaoLabel,
  integracaoChipFromRow,
} from '../utils/integracaoStatus';

const getDialogTitle = (row) => {
  const nsmpRef = row?.codigo || row?.nsmp || row?.nsmpProgramada;
  return nsmpRef ? `Integração - Nsmp ${nsmpRef}` : 'Integração';
};

const IntegracaoQuickView = ({
  row,
  open,
  onClose,
  onOpenNsmp,
  onEnviado,
  podeForcarEnvio,
  participacaoMode,
  userId,
}) => {
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);

  const detail = getIntegracaoDetail(row);
  const chip = integracaoChipFromRow(row);
  const eventosFromRow = getEventosResumoFromRow(row);
  const showTimeline = detail?.showTimeline && (row?.totalEventos ?? 0) > 0;
  const canForceSend =
    podeForcarEnvio
    && getIntegracaoStatus(row) === 'NOT_SYNC'
    && !!row?.id;

  const { eventos, loading, loadError } = useDigitacaoEventos(row?.id, {
    enabled: open && showTimeline && !eventosFromRow,
    participacaoMode,
    userId,
    fallbackIntegracao: row?.resumoIntegracao,
  });

  React.useEffect(() => {
    setSendError(null);
  }, [row?.id]);

  const timeline = eventosFromRow || eventos;

  const handleForceSend = async () => {
    if (!canForceSend || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await forcarEnvioIntegracaoNsmp(row.id);
      if (!result.ok) {
        setSendError('Não foi possível enviar a integração.');
        return;
      }
      onEnviado?.(row.id);
    } catch (error) {
      setSendError(
        error?.response?.data?.mensagem
        || error?.response?.data?.message
        || 'Não foi possível enviar a integração.',
      );
    } finally {
      setSending(false);
    }
  };

  if (!detail || !chip) return null;

  const statusAprovacaoLabel = getStatusAprovacaoLabel(detail.statusAprovacao);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={getDialogTitle(row)}
      readOnly
      maxWidth="sm"
      fullWidth
      headerAction={[
        {
          title: 'Abrir serviço',
          onClick: () => {
            onClose();
            onOpenNsmp(row.id);
          },
          show: !!(onOpenNsmp && row?.id),
          variant: 'outlined',
        },
        {
          title: sending ? 'Enviando…' : 'Enviar agora',
          onClick: handleForceSend,
          disabled: sending,
          show: canForceSend,
          variant: 'contained',
        },
      ]}
    >
      <Stack spacing={2}>
        <Box>{renderStatus(chip)}</Box>

        {detail.origemLabel && (
          <Typography variant="caption" color="text.secondary">
            {detail.origemLabel}
            {statusAprovacaoLabel ? ` · ${statusAprovacaoLabel}` : ''}
          </Typography>
        )}

        <Typography variant="body2">{detail.body}</Typography>

        {sendError && (
          <Typography variant="caption" color="error.main">
            {sendError}
          </Typography>
        )}

        {row?.resumoIntegracao?.erro && detail.severity === 'error' && (
          <Typography variant="caption" color="error.main">
            {row.resumoIntegracao.erro}
          </Typography>
        )}

        {detail.showReprocessHint && participacaoMode === 'ATIVO' && (
          <Typography variant="caption" color="text.secondary">
            Reprocessar disponível ao abrir o serviço.
          </Typography>
        )}

        {showTimeline && (
          <>
            <Divider />
            <Typography variant="subtitle2">Histórico de digitação</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : timeline.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {loadError
                  ? 'Histórico indisponível no momento.'
                  : 'Nenhum evento registrado.'}
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 280, overflow: 'auto', pr: 0.5 }}>
                <Stack spacing={1}>
                  {timeline.map((ev) => (
                    <Box
                      key={ev.ordem ?? ev.id}
                      sx={{
                        p: 1.25,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} fontSize="0.8125rem">
                        #{ev.ordem}{' '}
                        {formatDigitadoEm(ev.digitadoEm) || '—'}{' '}
                        · {ev.digitadorNome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ev.origemLabel}
                        {(ev.statusAprovacaoLabel || ev.statusAprovacao)
                          ? ` · ${ev.statusAprovacaoLabel || getStatusAprovacaoLabel(ev.statusAprovacao)}`
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    </DialogDefaultV2>
  );
};

IntegracaoQuickView.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onOpenNsmp: PropTypes.func,
  onEnviado: PropTypes.func,
  podeForcarEnvio: PropTypes.bool,
  participacaoMode: PropTypes.string,
  userId: PropTypes.string,
};

IntegracaoQuickView.defaultProps = {
  row: null,
  open: false,
  onOpenNsmp: undefined,
  onEnviado: undefined,
  podeForcarEnvio: false,
  participacaoMode: 'ATIVO',
  userId: null,
};

export default IntegracaoQuickView;
